import React, { useState, useEffect } from "react";
import styles from "./css/CitySettingsPage.module.css";
import { InputField } from "@/components/inputFields/inputFields";
import { useSelector } from "react-redux";
import { Button } from "@/components/Buttons";
import { RootState } from "@/store/store";
import { getCityAttributes, updateCityAttributes } from "@/api/cityAttributes";
import { CityAttributes } from "@/types";
import TargetYearChangeModal from "@/components/modals/TargetYearChangeModal";

export const CitySettingsPage: React.FC = () => {
  const city = useSelector((state: RootState) => state.entity.userCity);
  const [cityAttributes, setCityAttributes] = useState<CityAttributes | null>(null);
  const [targetYear, setTargetYear] = useState<number>(2040);
  const [originalTargetYear, setOriginalTargetYear] = useState<number>(2040);
  const [loading, setLoading] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  useEffect(() => {
    const fetchCityAttributes = async () => {
      if (!city?.id) return;
      
      try {
        const data = await getCityAttributes({ 
          city_id: city.id,
          neighborhood_id: undefined 
        });
        setCityAttributes(data);
        
        // Extract target_year from response (it's nested in the data property)
        const attrs = (data as any).data;
        const currentTargetYear = attrs?.target_year || 2040;
        setTargetYear(currentTargetYear);
        setOriginalTargetYear(currentTargetYear);
      } catch (error) {
        console.error("Failed to fetch city attributes:", error);
      }
    };

    fetchCityAttributes();
  }, [city?.id]);

  const handleSave = async () => {
    if (!city?.id) return;

    // Validate minimum 5-year gap
    const currentYear = new Date().getFullYear();
    if (targetYear - currentYear < 5) {
      alert(`שנת היעד חייבת להיות לפחות 5 שנים מהשנה הנוכחית (${currentYear})`);
      return;
    }

    setLoading(true);
    try {
      const response = await updateCityAttributes({
        city_id: city.id,
        neighborhood_id: undefined,
        target_year: targetYear,
      });

      // Check for warning message from backend
      if ((response as any).warning_message) {
        setWarningMessage((response as any).warning_message);
        setShowWarningModal(true);
      } else {
        alert("הגדרות העיר עודכנו בהצלחה");
      }

      setOriginalTargetYear(targetYear);
    } catch (error) {
      console.error("Failed to update city attributes:", error);
      alert("שגיאה בעדכון הגדרות העיר");
    } finally {
      setLoading(false);
    }
  };

  const hasChanges = targetYear !== originalTargetYear;

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h1 className={styles.title}>הגדרות עיר</h1>
        
        <div className={styles.settingsSection}>
          <h2 className={styles.sectionTitle}>שנת יעד לתחזיות</h2>
          <p className={styles.description}>
            שנת היעד משמשת לחישוב תחזיות צריכה, ייצור אנרגיה, ותשתיות טעינה. 
            שינוי שנת היעד יחשב מחדש את כל הנתונים.
          </p>
          
          <div className={styles.inputGroup}>
            <InputField
              inputType="number"
              data={targetYear}
              setData={(value) => setTargetYear(parseInt(value || '2040'))}
              id="target-year"
              placeholder="2040"
            />
            <p className={styles.hint}>
              חובה: מינימום 5 שנים מהשנה הנוכחית
            </p>
          </div>

          <div className={styles.actions}>
            <Button
              id="save-settings"
              text="שמור שינויים"
              onClick={handleSave}
              loading={loading}
              disabled={!hasChanges || loading}
              icon="no"
            />
          </div>
        </div>
      </div>

      <TargetYearChangeModal
        isOpen={showWarningModal}
        message={warningMessage}
        onClose={() => setShowWarningModal(false)}
      />
    </div>
  );
};
